// Visit-prep markdown generator. Output is a single self-contained .md a veteran
// can email ahead of an appointment, hand to a provider, upload to a patient
// portal, or print. The structure adapts to the visit type so a routine primary-
// care prep doesn't drag in C&P-specific nexus jargon, while C&P / IME visits
// still get the full service-connection scaffold.
import { THEORIES } from "@/lib/claimEnums";

export const VISIT_TYPES = [
  {
    id: "general",
    label: "General doctor visit",
    headline: "Doctor Visit — Prep Summary",
    claimRelated: false,
    reasonPlaceholder: "What you want to get out of this visit. e.g., follow-up on new medication, persistent symptom, lab review.",
    questionsPlaceholder: "Things I want to bring up:\nIs this med still right for me?\nWhat about my sleep — should we look at it?\nCan you refer me to a specialist for my knee?",
  },
  {
    id: "specialist",
    label: "Specialist consult",
    headline: "Specialist Consult — Prep Summary",
    claimRelated: false,
    reasonPlaceholder: "Why your PCP referred you / what you want this specialist to assess.",
    questionsPlaceholder: "Questions for the specialist:\nWhat are the diagnostic options?\nHow long until we see results?\nWhat are the risks of waiting vs. treating now?",
  },
  {
    id: "therapy",
    label: "Therapy / mental health",
    headline: "Therapy Visit — Prep Summary",
    claimRelated: false,
    reasonPlaceholder: "What's been going on since the last session — triggers, sleep, mood, function at home/work.",
    questionsPlaceholder: "Topics to cover:\nNew trigger from last week\nMedication side effects\nPartner / family stuff to talk through",
  },
  {
    id: "primary",
    label: "Primary care",
    headline: "Primary Care Visit — Prep Summary",
    claimRelated: false,
    reasonPlaceholder: "Annual physical, refill review, new symptom, or post-visit follow-up.",
    questionsPlaceholder: "Things to bring up:\nNew symptom: ____\nMed refills due: ____\nLab results from last visit\nReferral I still need",
  },
  {
    id: "cp_exam",
    label: "C&P Exam (VA)",
    headline: "C&P Exam — Intake Summary",
    claimRelated: true,
    reasonPlaceholder: "Which claim this C&P is for and the specific conditions being examined.",
    questionsPlaceholder: "Questions for the examiner:\nWhat is the most-likely etiology of my tinnitus?\nIs this at least as likely as not (50% or greater) connected to service?\nWhat additional diagnostics would clarify the nexus?",
  },
  {
    id: "ime",
    label: "Independent Medical Exam (IME)",
    headline: "Independent Medical Examination — Intake Summary",
    claimRelated: true,
    reasonPlaceholder: "What you're asking this IME to opine on (nexus / aggravation / severity / current diagnosis).",
    questionsPlaceholder: "Questions for the examiner:\nWhat is the most-likely etiology of my condition?\nIs this at least as likely as not caused by or aggravated by my service-connected primary?\nWhat additional diagnostic studies would clarify the nexus?",
  },
  {
    id: "other",
    label: "Other",
    headline: "Doctor Visit — Prep Summary",
    claimRelated: false,
    reasonPlaceholder: "Describe the visit purpose.",
    questionsPlaceholder: "Things to cover:",
  },
];

export function isClaimVisit(visitTypeDef) {
  if (!visitTypeDef) return false;
  return !!visitTypeDef.claimRelated;
}

function getVisitTypeDef(id) {
  return VISIT_TYPES.find((v) => v.id === id) || VISIT_TYPES[0];
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function safe(s) {
  if (s === null || s === undefined) return "—";
  const str = String(s).trim();
  return str === "" ? "—" : str;
}

function mdEscape(s) {
  return String(s ?? "").replace(/\|/g, "\\|").replace(/\n+/g, " ");
}

function bullet(s) { return `- ${s}`; }

export function buildVisitMarkdown({
  profile = {},
  master526ez = {},
  issues = [],
  evidence = [],
  appointments = [],
  prescriptions = [],
  statements = [],
  imeIntake = {},
}) {
  const lines = [];
  const veteranName = profile.name || master526ez.veteranName || "Patient";
  const branch = profile.branch || "—";
  const dob = master526ez.dateOfBirth || "—";
  const visitTypeDef = getVisitTypeDef(imeIntake.visitType);
  const claimRelated = isClaimVisit(visitTypeDef);

  const providerName = imeIntake.providerName || imeIntake.examinerName || "";
  const providerSpecialty = imeIntake.providerSpecialty || imeIntake.examinerSpecialty || "";
  const discussionQuestions = imeIntake.discussionQuestions || imeIntake.examinerQuestions || "";

  // ----------------------------------------------------------------- HEADER
  lines.push(`# ${visitTypeDef.headline}`);
  lines.push("");
  if (providerName || providerSpecialty) {
    lines.push(`**Prepared for:** ${providerName || (claimRelated ? "Independent Medical Examiner" : "Provider")}` +
      (providerSpecialty ? ` *(${providerSpecialty})*` : ""));
  }
  lines.push(`**Prepared by:** ${veteranName}`);
  if (imeIntake.visitDate) lines.push(`**Visit date:** ${fmtDate(imeIntake.visitDate)}`);
  lines.push(`**Date prepared:** ${fmtDate(new Date())}`);
  lines.push(`**Visit type:** ${visitTypeDef.label}`);
  lines.push("");
  lines.push("> This document was prepared by the patient using ClaimOps VA. It summarizes their history, current concerns, and questions for this visit. All severity scores and impact descriptions are subjective self-reports. Medical opinions remain the sole judgment of the provider.");
  lines.push("");
  lines.push("---");
  lines.push("");

  // -------------------------------------------------------- 1. PATIENT ID
  lines.push("## 1. Patient identification");
  lines.push("");
  lines.push(`| Field | Value |`);
  lines.push(`| --- | --- |`);
  lines.push(`| Name | ${mdEscape(safe(veteranName))} |`);
  lines.push(`| Date of birth | ${mdEscape(safe(dob))} |`);
  if (claimRelated) {
    lines.push(`| Branch of service | ${mdEscape(safe(branch))} |`);
    lines.push(`| Service start | ${mdEscape(safe(master526ez.serviceStart))} |`);
    lines.push(`| Service end | ${mdEscape(safe(master526ez.serviceEnd))} |`);
    lines.push(`| Final paygrade | ${mdEscape(safe(profile.paygrade))} |`);
  }
  if (master526ez.email) lines.push(`| Email | ${mdEscape(master526ez.email)} |`);
  if (master526ez.phone) lines.push(`| Phone | ${mdEscape(master526ez.phone)} |`);
  lines.push("");

  // -------------------------------------------- 2. REASON FOR VISIT
  if (imeIntake.visitReason) {
    lines.push("## 2. Reason for this visit");
    lines.push("");
    lines.push(imeIntake.visitReason.split("\n").map((p) => p.trim()).filter(Boolean).join("\n\n"));
    lines.push("");
  }

  // -------------------------------------------- 3. CONDITIONS / CONCERNS
  const conditionsHeading = claimRelated
    ? "Conditions under examination"
    : "Active conditions / ongoing concerns";
  const sectionN = imeIntake.visitReason ? 3 : 2;
  lines.push(`## ${sectionN}. ${conditionsHeading}`);
  lines.push("");
  if (issues.length === 0) {
    lines.push("_No conditions on file._");
  } else if (claimRelated) {
    lines.push("| # | Condition (claimed as) | DC code | Theory of service connection |");
    lines.push("| --- | --- | --- | --- |");
    issues.forEach((iss, i) => {
      lines.push(`| ${i + 1} | ${mdEscape(iss.claimedAs || "(untitled)")} | ${mdEscape(iss.code || "—")} | ${mdEscape(iss.theory || THEORIES.direct)} |`);
    });
    lines.push("");
    issues.forEach((iss, i) => {
      lines.push(`### ${sectionN}.${i + 1}. ${iss.claimedAs || "(untitled)"} *(DC ${iss.code || "?"})*`);
      lines.push("");
      lines.push(`**Theory:** ${safe(iss.theory)}`);
      if (iss.secondaryTo) lines.push(`**Claimed as secondary to:** ${safe(iss.secondaryTo)}`);
      if (iss.secondaryReason) lines.push(`**Secondary rationale:** ${safe(iss.secondaryReason)}`);
      if (iss.bilateralSide) lines.push(`**Side:** ${safe(iss.bilateralSide)}`);
      lines.push("");
      if (iss.onset) { lines.push(`**Onset / first noticed:** ${iss.onset}`); lines.push(""); }
      if (iss.functionalImpact) { lines.push(`**Functional impact (patient's report):**`); lines.push(""); lines.push(`> ${iss.functionalImpact.replace(/\n/g, "\n> ")}`); lines.push(""); }
      const linkedStmts = statements.filter((s) => s.conditionId === iss.id || s.conditionName === iss.claimedAs);
      if (linkedStmts.length > 0) {
        lines.push(`**Statements attached:** ${linkedStmts.map((s) => `${s.type || "personal"} (${fmtDate(s.createdAt)})`).join(", ")}`);
        lines.push("");
      }
    });
  } else {
    // Routine visit — keep it short and friendly, no DC code dump.
    lines.push("| # | Condition | Onset | Functional impact |");
    lines.push("| --- | --- | --- | --- |");
    issues.forEach((iss, i) => {
      lines.push(`| ${i + 1} | ${mdEscape(iss.claimedAs || "(untitled)")} | ${mdEscape(safe(iss.onset))} | ${mdEscape(safe(iss.functionalImpact))} |`);
    });
    lines.push("");
  }

  // ------------------------------------------------------ SERVICE HISTORY
  // Only relevant for claim-related visits.
  let nextSection = sectionN + 1;
  if (claimRelated && imeIntake.serviceHistory) {
    lines.push(`## ${nextSection}. Service history (patient narrative)`);
    lines.push("");
    lines.push(imeIntake.serviceHistory.split("\n").map((p) => p.trim()).filter(Boolean).join("\n\n"));
    lines.push("");
    nextSection += 1;
  }

  // ------------------------------------------------ TIMELINE
  const tlEvents = [];
  if (claimRelated && master526ez.serviceStart) tlEvents.push({ date: master526ez.serviceStart, event: `Service start (${branch})`, source: "DD-214 / profile" });
  if (claimRelated && master526ez.serviceEnd) tlEvents.push({ date: master526ez.serviceEnd, event: `Service end / separation`, source: "DD-214 / profile" });
  (imeIntake.timeline || []).forEach((e) => tlEvents.push({ date: e.date, event: e.event, source: e.source || "Patient note" }));
  appointments.filter((a) => a.completed).forEach((a) => {
    tlEvents.push({
      date: a.datetime ? a.datetime.slice(0, 10) : "",
      event: `${a.type === "cp_exam" ? "C&P Exam" : "Medical visit"}: ${a.title}${a.provider ? ` (${a.provider})` : ""}`,
      source: "Appointment record",
    });
  });
  tlEvents.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  if (tlEvents.length > 0 || claimRelated) {
    lines.push(`## ${nextSection}. ${claimRelated ? "Chronological timeline" : "Recent timeline"}`);
    lines.push("");
    if (tlEvents.length === 0) {
      lines.push("_No timeline events recorded._");
    } else {
      lines.push("| Date | Event | Source |");
      lines.push("| --- | --- | --- |");
      tlEvents.forEach((e) => {
        lines.push(`| ${mdEscape(fmtDate(e.date))} | ${mdEscape(e.event)} | ${mdEscape(e.source)} |`);
      });
    }
    lines.push("");
    nextSection += 1;
  }

  // --------------------------------------------------- DOCUMENTS / RECORDS
  lines.push(`## ${nextSection}. Records being brought (appendix manifest)`);
  lines.push("");
  const docs = [
    ...evidence.map((ev) => ({
      title: ev.title || ev.label || "Untitled evidence",
      date: ev.date || ev.dateOfRecord || "",
      source: ev.source || ev.type || "Patient-supplied",
      summary: ev.summary || "",
    })),
    ...(imeIntake.documents || []),
  ];
  if (docs.length === 0) {
    lines.push("_No records recorded._");
  } else {
    lines.push("| # | Document | Date of record | Source / type | Summary |");
    lines.push("| --- | --- | --- | --- | --- |");
    docs.forEach((d, i) => {
      lines.push(`| ${i + 1} | ${mdEscape(d.title)} | ${mdEscape(fmtDate(d.date))} | ${mdEscape(d.source)} | ${mdEscape(d.summary || "—")} |`);
    });
  }
  lines.push("");
  nextSection += 1;

  // ----------------------------------------------- TALKING POINTS / CHECKLISTS
  if (issues.length > 0) {
    lines.push(`## ${nextSection}. ${claimRelated ? "Nexus checklists (per condition)" : "Talking points (per condition)"}`);
    lines.push("");
    issues.forEach((iss, i) => {
      lines.push(`### ${nextSection}.${i + 1}. ${iss.claimedAs}`);
      lines.push("");
      const checks = getTalkingPoints(iss, imeIntake.checklists?.[iss.id] || {}, visitTypeDef);
      checks.forEach((c) => lines.push(`- [${c.done ? "x" : " "}] ${c.label}${c.note ? ` — _${c.note}_` : ""}`));
      lines.push("");
    });
    nextSection += 1;
  }

  // ----------------------------------------------- QUESTIONS
  lines.push(`## ${nextSection}. ${claimRelated ? "Questions for the examiner" : "Topics / questions to cover"}`);
  lines.push("");
  const questions = discussionQuestions.split("\n").map((q) => q.trim()).filter(Boolean);
  if (questions.length === 0) {
    lines.push("_No pre-written questions._");
  } else {
    questions.forEach((q) => lines.push(bullet(q)));
  }
  lines.push("");
  nextSection += 1;

  // ----------------------------------------------- CURRENT MEDICATIONS
  const activeMeds = prescriptions.filter((rx) => !rx.archived);
  if (activeMeds.length > 0) {
    lines.push(`## ${nextSection}. Current medications`);
    lines.push("");
    lines.push("| Medication | Dosage | Prescriber | Last filled | Days supply | Refills remaining |");
    lines.push("| --- | --- | --- | --- | --- | --- |");
    activeMeds.forEach((rx) => {
      lines.push(`| ${mdEscape(rx.medName)} | ${mdEscape(safe(rx.dosage))} | ${mdEscape(safe(rx.prescriber))} | ${mdEscape(fmtDate(rx.lastFilled))} | ${mdEscape(safe(rx.daysSupply))} | ${mdEscape(safe(rx.refillsRemaining))} |`);
    });
    lines.push("");
  }

  lines.push("---");
  lines.push("");
  lines.push("_Generated by ClaimOps VA. Severity scores and functional-impact descriptions are subjective self-reports. Medical opinions remain the sole judgment of the provider._");
  lines.push("");

  return lines.join("\n");
}

/**
 * Per-condition talking points / nexus checklist. For claim-related visits this
 * is the full nexus scaffold (direct / secondary / aggravation / presumptive).
 * For routine visits it's a simple "what to mention to the doctor" list.
 */
export function getTalkingPoints(issue, completionState = {}, visitTypeDef = null) {
  const claimRelated = isClaimVisit(visitTypeDef);
  const items = [];

  if (claimRelated) {
    // Always-required for any nexus opinion
    items.push({ id: "current-dx", label: "Current diagnosis dated within the last 12 months" });
    items.push({ id: "str-relevant", label: "Relevant Service Treatment Record (STR) pages provided" });
    items.push({ id: "veteran-statement", label: "Veteran personal statement (in-service onset + ongoing symptoms)" });

    const theory = (issue?.theory || "").toLowerCase();
    if (theory.includes("direct")) {
      items.push({ id: "in-service-event", label: "Documented in-service event / exposure / injury" });
      items.push({ id: "buddy-statement", label: "Buddy or lay-witness statement corroborating the event" });
      items.push({ id: "continuity", label: "Continuity-of-symptoms evidence from separation to present" });
    } else if (theory.includes("secondary")) {
      items.push({ id: "primary-sc", label: "Documentation that the primary condition is already service-connected" });
      items.push({ id: "medical-link", label: "Medical literature / clinician statement linking primary → secondary" });
      items.push({ id: "timeline", label: "Timeline showing primary condition pre-dated this one" });
    } else if (theory.includes("aggrav")) {
      items.push({ id: "pre-service", label: "Documentation of pre-service baseline severity" });
      items.push({ id: "in-service-worsen", label: "Documentation of in-service worsening (beyond natural progression)" });
      items.push({ id: "post-service-permanence", label: "Post-service evidence of permanent worsening" });
    } else if (theory.includes("presumptive") || theory.includes("pact")) {
      items.push({ id: "exposure-location", label: "Documentation of qualifying service location / dates (PACT, AO, Camp Lejeune, etc.)" });
      items.push({ id: "diagnosis-window", label: "Diagnosis within the presumptive window (if applicable)" });
    }
  } else {
    // Routine visit talking points — what to actually say to the doctor.
    items.push({ id: "symptoms-now", label: "Current symptoms (what's bothering me right now)" });
    items.push({ id: "symptom-change", label: "Anything that's changed since last visit (better / worse / new)" });
    items.push({ id: "trigger", label: "Triggers I've noticed (food, stress, sleep, weather, activity)" });
    items.push({ id: "function-impact", label: "How it's affecting daily function (work, sleep, mood, family)" });
    items.push({ id: "med-effects", label: "Medication effects or side-effects to flag" });
  }

  // Specialty-specific add-ons (apply to both modes — relevant info either way).
  const code = String(issue?.code || "").trim();
  if (code === "9411" || code === "9434") items.push({ id: "psych-eval", label: "Recent psych evaluation (DBQ or equivalent)" });
  if (code === "6260" || code === "6100") items.push({ id: "audiogram", label: "Current audiogram with Maryland CNC speech discrimination" });
  if (code === "6847") items.push({ id: "sleep-study", label: "Sleep study (in-lab or home) confirming AHI / treatment indication" });
  if (code === "8100") items.push({ id: "migraine-log", label: "Headache log showing prostrating attack frequency" });

  return items.map((it) => ({
    ...it,
    done: !!completionState[it.id],
    note: completionState[`${it.id}__note`] || "",
  }));
}

// ----- Back-compat aliases for any external import sites that still use the old names.
export const buildImeMarkdown = buildVisitMarkdown;
export const getNexusChecklist = (issue, completionState = {}) =>
  getTalkingPoints(issue, completionState, { claimRelated: true });
